import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import apiClient from 'src/helpers/apiClient';
import { getFormattedErr } from 'src/helpers';
import type {
  AssistantConversation,
  AssistantMessage,
  AssistantPage,
  AssistantUsage,
} from 'src/redux/types/assistant';

const errorKey = (err: any): string =>
  err?.response?.data?.error ?? 'something_went_wrong';

export const sendAssistantMessage = createAsyncThunk(
  'assistant/sendMessage',
  async (
    {
      message,
      conversationId,
      page,
    }: { message: string; conversationId: string | null; page: AssistantPage },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getAssistantApi()
      ).assistantApiSendAssistantMessage({
        sendAssistantMessageBody: {
          message,
          conversation_id: conversationId,
          page,
        },
      });
      return response.data as unknown as {
        conversation: AssistantConversation;
        user_message: AssistantMessage;
        assistant_message: AssistantMessage;
        usage: AssistantUsage;
      };
    } catch (err: any) {
      // The panel explains these in place; a toast would repeat it.
      const key = errorKey(err);
      if (
        ![
          'assistant_daily_limit_reached',
          'rescue_chat_limit_reached',
          'assistant_busy',
        ].includes(key)
      ) {
        enqueueSnackbar(getFormattedErr(key), { variant: 'error' });
      }
      return rejectWithValue(key);
    }
  },
);

export const fetchAssistantUsage = createAsyncThunk(
  'assistant/fetchUsage',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getAssistantApi()
      ).assistantApiGetAssistantUsage();
      return response.data as AssistantUsage;
    } catch (err: any) {
      return rejectWithValue(errorKey(err));
    }
  },
);

export const fetchAssistantConversations = createAsyncThunk(
  'assistant/fetchConversations',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getAssistantApi()
      ).assistantApiListAssistantConversations();
      return (response.data.conversations ?? []) as AssistantConversation[];
    } catch (err: any) {
      return rejectWithValue(errorKey(err));
    }
  },
);

export const fetchAssistantConversation = createAsyncThunk(
  'assistant/fetchConversation',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getAssistantApi()
      ).assistantApiGetAssistantConversation({ conversationId });
      return {
        conversation: response.data.conversation as AssistantConversation,
        messages: (response.data.messages ?? []) as AssistantMessage[],
      };
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(errorKey(err)), { variant: 'error' });
      return rejectWithValue(errorKey(err));
    }
  },
);

export const deleteAssistantConversation = createAsyncThunk(
  'assistant/deleteConversation',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      await (
        await apiClient.getAssistantApi()
      ).assistantApiDeleteAssistantConversation({ conversationId });
      return conversationId;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(errorKey(err)), { variant: 'error' });
      return rejectWithValue(errorKey(err));
    }
  },
);

/** The socket fallback: re-reads one reply while its pushes are not arriving. */
export const pollAssistantMessage = createAsyncThunk(
  'assistant/pollMessage',
  async (messageId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getAssistantApi()
      ).assistantApiGetAssistantMessage({ messageId });
      return response.data as unknown as AssistantMessage;
    } catch (err: any) {
      return rejectWithValue(errorKey(err));
    }
  },
);

export const decideAssistantAction = createAsyncThunk(
  'assistant/decideAction',
  async (
    {
      messageId,
      actionId,
      decision,
    }: { messageId: string; actionId: string; decision: 'confirm' | 'cancel' },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getAssistantApi()
      ).assistantApiDecideAssistantAction({
        messageId,
        actionId,
        decideAssistantActionBody: { decision },
      });
      return response.data as unknown as AssistantMessage;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(errorKey(err)), { variant: 'error' });
      return rejectWithValue(errorKey(err));
    }
  },
);

/**
 * Asks for the chat the app opens after a Pro checkout is left unfinished.
 * The server decides whether one opens (web, not Pro, not one already today):
 * `conversation` is null when it does not. Silent either way -- the buyer
 * did not ask for it.
 */
export const startCheckoutRescue = createAsyncThunk(
  'assistant/startCheckoutRescue',
  async (
    { reason, path }: { reason?: string; path: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getAssistantApi()
      ).assistantApiStartCheckoutRescue({
        startCheckoutRescueBody: { reason, page: { path } },
      });
      return response.data as unknown as {
        conversation: AssistantConversation | null;
        messages: AssistantMessage[];
      };
    } catch (err: any) {
      return rejectWithValue(errorKey(err));
    }
  },
);
